import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import moment from 'moment'
import PerfectScrollbar from 'react-perfect-scrollbar'

// evertel
import { useService } from '@evertel/di'
import { DocumentServiceProvider } from '@evertel/service'
import { Document, WidgetsProvider } from '@evertel/schema-parser'
import {
    Modal, ModalHeader, ModalBody,
    Card, CardHeader, CardBody, CardFooter,
    Row, Col, Text, Icon, Collapse
} from '@evertel/web/ui'
import { DocumentVersionHistoryController, Version } from '@evertel/document'
import { APIDataBlueUser, APIDataDocumentSchema } from '@evertel/types'
import { DocumentIcon } from '../elements/DocumentIcon'
import { DocumentOwnerDisplay } from '../elements/DocumentOwnerDisplay'
import { ReadWidgets } from '../schema-widgets'
import { DocumentVersionCard } from './DocumentVersionCard'

interface DocumentVersionHistoryModalProps {
    visible: boolean
    documentId: number
    schema?: APIDataDocumentSchema
    media?: any[]
    currentOwner?: APIDataBlueUser
    onClose: () => void
}

const formatOwnerName = (owner?: APIDataBlueUser) => {
    if (!owner) return undefined

    return (owner.firstName?.trim() || owner.lastName?.trim())
        ? `${owner.firstName || ''} ${owner.lastName || ''}`.trim()
        : (owner.email || 'Unknown')
}

interface VersionGroup {
    key: string
    label: string
    items: { version: Version, index: number }[]
}

const getDateGroupLabel = (date: Date) => {
    const m = moment(date)
    if (m.isSame(moment(), 'day')) return 'Today'
    if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday'
    return m.format('MMMM Do, YYYY')
}

// newest-first, grouped by calendar day
const groupVersionsByDate = (versions: Version[]): VersionGroup[] => {
    const groups: VersionGroup[] = []

    for (let index = versions.length - 1; index >= 0; index--) {
        const version = versions[index]
        const key = moment(version.createdDate).format('YYYY-MM-DD')

        let group = groups.find(g => g.key === key)
        if (!group) {
            group = { key, label: getDateGroupLabel(version.createdDate), items: [] }
            groups.push(group)
        }

        group.items.push({ version, index })
    }

    return groups
}

const DocumentVersionHistoryModal: React.FC<DocumentVersionHistoryModalProps> = observer(({
    visible,
    documentId,
    schema,
    media,
    currentOwner,
    onClose
}) => {

    const documentVersionHistoryController = useService(DocumentVersionHistoryController, [])
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
    const [expandedGroupKeys, setExpandedGroupKeys] = useState<Set<string>>(new Set())

    useEffect(() => {
        if (visible && documentId) {
            documentVersionHistoryController.init(documentId)
        }
    }, [visible, documentId])

    // separate effect: ACL data (and therefore the real owner) can resolve
    // slightly after the modal opens, independently of the history fetch above
    useEffect(() => {
        documentVersionHistoryController.setCurrentOwner(currentOwner?.id, formatOwnerName(currentOwner))
    }, [currentOwner])

    useEffect(() => {
        if (!visible) {
            setSelectedIndex(null)
        }
    }, [visible])

    const versions = documentVersionHistoryController.versions
    const groups = groupVersionsByDate(versions)

    useEffect(() => {
        if (versions.length && expandedGroupKeys.size === 0) {
            setExpandedGroupKeys(new Set(groups.map(g => g.key)))
        }
    }, [versions.length])

    if (!visible || !versions.length) return null

    const effectiveIndex = selectedIndex ?? versions.length - 1
    const selectedVersion = versions[effectiveIndex]
    const diff = documentVersionHistoryController.diffAgainstPrevious(effectiveIndex)

    const isNewest = effectiveIndex === versions.length - 1
    const isOldest = effectiveIndex === 0

    const toggleGroup = (key: string) => {
        setExpandedGroupKeys(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    // versions before the first real ownership transfer have no event to read a
    // name from — fall back to the document's creator (a real BlueUser object,
    // which DocumentOwnerDisplay formats on its own). The current version falling
    // back to null means genuinely no owner (deleted user) or ACLs still loading.
    const selectedOwner = selectedVersion.ownerName
        ? { firstName: selectedVersion.ownerName, lastName: '' }
        : selectedVersion.isCurrent
            ? null
            : (documentVersionHistoryController.createdEntry?.createdBy ?? null)

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            alignment="center"
            size="xxl">
            <ModalHeader />
            <ModalBody className="version-history-modal-body">
                <Row className="h-100">
                    <Col xs={9} className="h-100 pe-2 d-flex flex-column">
                        <Row
                            align="between"
                            valign="center"
                            className="version-preview-banner bg-info-subtle rounded-2 p-2 mb-2">
                            <button
                                type="button"
                                disabled={isNewest}
                                aria-label="Newer version"
                                onClick={() => setSelectedIndex(effectiveIndex + 1)}
                                className={classNames('border-0 bg-transparent px-2', { 'opacity-25': isNewest })}>
                                <Icon name="chevron-left" />
                            </button>
                            <Text>
                                Previewing {selectedVersion.isCurrent
                                    ? <strong>Current Version</strong>
                                    : moment(selectedVersion.createdDate).format('MMMM Do, YYYY, HH:mm')}
                            </Text>
                            <button
                                type="button"
                                disabled={isOldest}
                                aria-label="Older version"
                                onClick={() => setSelectedIndex(effectiveIndex - 1)}
                                className={classNames('border-0 bg-transparent px-2', { 'opacity-25': isOldest })}>
                                <Icon name="chevron-right" />
                            </button>
                        </Row>

                        <Card className="version-history-panel">
                            <CardHeader>
                                <Row align="between" valign="center" className="w-100">
                                    <Row valign="center">
                                        {schema &&
                                            <DocumentIcon
                                                schema={schema}
                                                width={32}
                                                height={32}
                                                className="me-2"
                                            />
                                        }
                                        <Text
                                            tag="h5"
                                            bold
                                            className={classNames('mb-0', { 'bg-warning-subtle rounded-2 px-1': diff.nameChanged })}>
                                            {selectedVersion.name}
                                        </Text>
                                    </Row>
                                    <DocumentOwnerDisplay
                                        type="label"
                                        selectedOwner={selectedOwner}
                                        isChanged={diff.ownerChanged}
                                    />
                                </Row>
                            </CardHeader>
                            <CardBody>
                                <PerfectScrollbar
                                    options={{ suppressScrollX: true }}
                                    style={{ height: '100%' }}>
                                    <DocumentServiceProvider services={[{ id: 'meta', value: { media: media ?? [] } }]}>
                                        <WidgetsProvider value={ReadWidgets}>
                                            <Document
                                                key={effectiveIndex}
                                                layout="full"
                                                schema={schema?.schema}
                                                schemaId={schema?.id}
                                                documentId={documentId}
                                                value={selectedVersion.data}
                                                changedFieldIds={diff.changedFieldIds}
                                            />
                                        </WidgetsProvider>
                                    </DocumentServiceProvider>
                                </PerfectScrollbar>
                            </CardBody>
                            <CardFooter className="d-flex align-items-center justify-content-center">
                                <Text size={12} color="muted">
                                    Changes made on the selected date are highlighted in{' '}
                                    <span className="bg-warning-subtle rounded-2 px-1">yellow</span>.
                                </Text>
                            </CardFooter>
                        </Card>
                    </Col>

                    <Col xs={3} className="h-100 ps-2">
                        <Card className="version-history-panel">
                            <CardHeader>
                                <Text tag="h5" bold className="mb-0">
                                    Version History
                                </Text>
                            </CardHeader>
                            <CardBody>
                                <PerfectScrollbar
                                    options={{ suppressScrollX: true }}
                                    style={{ height: '100%' }}>
                                    {groups.map(group => (
                                        <div key={group.key} className="mb-2">
                                            <Row
                                                role="button"
                                                tabIndex={0}
                                                aria-expanded={expandedGroupKeys.has(group.key)}
                                                onClick={() => toggleGroup(group.key)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault()
                                                        toggleGroup(group.key)
                                                    }
                                                }}
                                                valign="center"
                                                className="py-2">
                                                <Icon
                                                    name={expandedGroupKeys.has(group.key) ? 'chevron-up' : 'chevron-down'}
                                                    size={12}
                                                    className="me-2"
                                                />
                                                <Text bold size={13}>
                                                    {group.label}
                                                </Text>
                                            </Row>
                                            <Collapse visible={expandedGroupKeys.has(group.key)}>
                                                {group.items.map(({ version, index }) => (
                                                    <DocumentVersionCard
                                                        key={index}
                                                        createdBy={version.createdBy}
                                                        createdDate={version.createdDate}
                                                        isCurrent={version.isCurrent}
                                                        isOldest={index === 0}
                                                        isViewing={index === effectiveIndex}
                                                        onClick={() => setSelectedIndex(index)}
                                                    />
                                                ))}
                                            </Collapse>
                                        </div>
                                    ))}
                                </PerfectScrollbar>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </ModalBody>
        </Modal>
    )
})

export { DocumentVersionHistoryModal }
